// MUI components
import {
    Autocomplete,
    Box,
    Button,
    CircularProgress,
    InputAdornment,
    Link,
    TextField,
    Typography,
} from "@mui/material";

// Components
import { Permissions } from "./components/Permissions";
import CustomIcon from "components/CustomIcon";
import NavButton from "components/HeaderNavigation/NavButton";
import { FieldHintButton } from "components/FieldHintButton";
import CloseIcon from "@mui/icons-material/Close";
import { SnackBar } from "components/SnackBar";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";

// Hooks
import { FormattedMessage, useIntl } from "react-intl";

// Services
import { CompanySearchHub } from "services";

// Utils

// Enums
import icons from "enums/icons";

// Styles
import useStyles from "./HubManagementNew.styles";
import useHubManagementNew from "./useHubManagementNew";
import { isEmpty } from "lodash";

const HubManagementNew = () => {
    const intl = useIntl();
    const classes = useStyles();

    const {
        open,
        options,
        bodyContainerRef,
        selectedHub,
        selectedPermission,
        permissionsBundle,
        isFetching,
        disablePermissions,
        handleValidateCRMLink,
        handleExitPage,
        handleGoNext,
        setOpen,
        getOptionLabel,
        onChange,
        onInputChange,
        handleClickClearFilter,
        renderOption,
        PaperComponent,
        handleSelectPermission,
        handleCRMGoBack,
        onClickOrgDomain,
    } = useHubManagementNew();

    const isNextButtonDisabled =
        selectedHub === null || isEmpty(selectedPermission.ldPermission) || isEmpty(selectedPermission.pacePermission);

    const crmIdentifier = selectedHub?.identifiers.find((identifier) => identifier.type === "crm");
    const openSnackBar = Boolean(selectedHub && !crmIdentifier);

    return (
        <Box ref={bodyContainerRef} style={{ position: "relative", width: "100%" }}>
            <Box className={classes.headerWrapper}>
                <Typography variant="h4">
                    {intl.formatMessage({
                        id: "hubManagement.createNew.title",
                    })}
                </Typography>
                <Box>
                    <NavButton onClick={handleExitPage} isBackButton translationId="hubManagement.navButtons.exit" />
                    <NavButton
                        onClick={handleGoNext}
                        disabled={isNextButtonDisabled}
                        translationId="hubManagement.navButtons.next"
                    />
                </Box>
            </Box>

            <Box className={classes.bodyContainer}>
                <Typography variant="subtitle1" className={classes.subtitle}>
                    {intl.formatMessage({ id: "hubManagement.createNew.description" })}
                </Typography>

                <Box className={classes.selectWrapper}>
                    <Box className={classes.selectAndTitle}>
                        <Typography variant="h6" className={classes.selectTitle}>
                            {intl.formatMessage({ id: "hubManagement.createNew.selectOrganisation.title" })}
                        </Typography>

                        <Box sx={{ flex: 1 }}>
                            <Autocomplete
                                id="hub-search-organisation"
                                classes={{
                                    root: classes.autocompleteRoot,
                                }}
                                value={selectedHub}
                                options={options.data}
                                open={open && options.status === "done"}
                                onOpen={() => setOpen(true)}
                                onClose={() => setOpen(false)}
                                getOptionLabel={getOptionLabel}
                                onChange={onChange}
                                onInputChange={onInputChange}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        autoComplete="off"
                                        placeholder="Search Organisation"
                                        InputProps={{
                                            ...params.InputProps,
                                            startAdornment: (
                                                <CustomIcon
                                                    icon={icons.searchInverted}
                                                    className={classes.searchIcon}
                                                />
                                            ),
                                            endAdornment: (
                                                <InputAdornment position="end">
                                                    {options.status === "fetching" && (
                                                        <CircularProgress
                                                            color="primary"
                                                            className={classes.circularProgress}
                                                        />
                                                    )}
                                                    {options.status !== "fetching" && selectedHub !== null && (
                                                        <CloseIcon
                                                            fontSize="small"
                                                            sx={{ cursor: "pointer" }}
                                                            onClick={handleClickClearFilter}
                                                        />
                                                    )}
                                                    <FieldHintButton iconSize={24} iconColor="info.main">
                                                        <FormattedMessage id="field.hint.company.search" />
                                                    </FieldHintButton>
                                                </InputAdornment>
                                            ),
                                        }}
                                    />
                                )}
                                renderOption={renderOption}
                                PaperComponent={PaperComponent}
                                filterOptions={(options: CompanySearchHub[], state: object) => options}
                            />
                        </Box>
                    </Box>

                    <Box>
                        {selectedHub && (selectedHub?.domains[0]?.domain || selectedHub?.orgs[0]?.crm_id) ? (
                            <Box className={classes.crmContainer}>
                                <Typography variant="body1" marginRight="8px" fontSize={12}>
                                    CRM Website:
                                </Typography>
                                {selectedHub?.domains[0].domain && (
                                    <Link
                                        marginRight="8px"
                                        className={classes.companyLink}
                                        target="_blank"
                                        href={`http://${selectedHub?.domains[0]?.domain}`}
                                        onClick={onClickOrgDomain}
                                        rel="noopener noreferrer"
                                    >
                                        {selectedHub?.domains[0]?.domain}
                                    </Link>
                                )}

                                {selectedHub?.orgs[0]?.crm_id && (
                                    <>
                                        <span>| </span>
                                        <Box marginLeft="8px" display="flex">
                                            <CustomIcon icon={icons.share} className={classes.shareIcon} />
                                            <Link
                                                component="button"
                                                variant="body2"
                                                className={classes.companyLink}
                                                onClick={handleValidateCRMLink}
                                            >
                                                Link To CRM
                                            </Link>
                                        </Box>
                                    </>
                                )}
                            </Box>
                        ) : null}
                    </Box>
                </Box>

                <Permissions
                    isFetching={isFetching}
                    disabled={disablePermissions}
                    permissionsBundle={permissionsBundle}
                    selectedPermission={selectedPermission}
                    onSelectPermission={handleSelectPermission}
                    introText={intl.formatMessage({ id: "hubManagement.permissions.description" })}
                />
            </Box>

            <SnackBar
                maxWidth={497}
                shouldOpen={openSnackBar}
                anchorElement={null}
                backgroundBlurred={true}
                transformOrigin={{ vertical: "center", horizontal: "center" }}
                anchorOrigin={{ vertical: "center", horizontal: "center" }}
                handleInfoClose={handleCRMGoBack}
                title={
                    <Box display="flex">
                        <InfoOutlinedIcon sx={{ rotate: "180deg", alignSelf: "center", marginRight: "4px" }} />
                        <Typography variant="h6" fontWeight={600}>
                            {intl.formatMessage({ id: "hubManagement.createNew.noCrmLink.messageTitle" })}
                        </Typography>
                    </Box>
                }
                content={
                    <Box px={1}>
                        <Typography variant="subtitle1" sx={{ lineHeight: 1.25 }}>
                            {intl.formatMessage({ id: "hubManagement.createNew.noCrmLink.message" })}
                        </Typography>
                        <Box sx={{ display: "flex", justifyContent: "flex-end", marginTop: "8px" }}>
                            <Button variant="outlined" size="small" onClick={handleCRMGoBack}>
                                <Typography>
                                    {intl.formatMessage({ id: "hubManagement.createNew.noCrmLink.button" })}
                                </Typography>
                            </Button>
                        </Box>
                    </Box>
                }
            />
        </Box>
    );
};

export default HubManagementNew;
